import { createApiClient } from "~/api/apiClient";
import type { components } from "~/api/generated/api-server-types";
import {
  type ConversationPhase,
  type ConversationProgress,
  normalizeProgress,
} from "./useConversationProgress";

type StatusResponse = components["schemas"]["ConversationProgress"];

function isConversationPhase(phase: string): phase is ConversationPhase {
  return phase === "responding" || phase === "compacting" || phase === "idle";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Checkpoint events are `{event, data}` pairs; the schema types them loosely. */
function toProgressEvents(
  events: StatusResponse["events"],
): ConversationProgress["events"] {
  const parsed: ConversationProgress["events"] = [];
  for (const item of events ?? []) {
    if (typeof item.event === "string" && isRecord(item.data))
      parsed.push({ event: item.event, data: item.data });
  }
  return parsed;
}

function fromStatusResponse(data: StatusResponse): ConversationProgress {
  return {
    active: data.active,
    ...(isConversationPhase(data.phase) ? { phase: data.phase } : {}),
    turn_id: data.turn_id ?? null,
    started_at: data.started_at ?? null,
    events: toProgressEvents(data.events),
    user_message_id: data.user_message_id ?? null,
    assistant_message_id: data.assistant_message_id ?? null,
  };
}

/**
 * Read the turn lock from the api-server, which never takes an Indy Lambda
 * instance away from a streaming reply. Throws on failure; the poller in
 * `useConversationProgress` owns retry and backoff.
 */
export async function fetchConversationStatus(
  conversationId: string,
  signal: AbortSignal,
): Promise<ConversationProgress> {
  const { data, response } = await createApiClient().POST(
    "/indy/conversation/status",
    {
      body: { conversation_id: conversationId, include_activity: false },
      signal,
    },
  );
  if (!data)
    throw new Error(`Conversation status unavailable (${response.status})`);
  return normalizeProgress(fromStatusResponse(data));
}
